import { useCallback, useEffect, useRef, useState } from "react";

import { getFleetVehiclesAPI, getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { FleetVehicle, FleetVehicleRow } from "services/api/fleet/vehicles/types";
import { initialPagination } from "ui/components/Table/consts";

const PAGE_SIZE = 20;

/**
 * Paginirani dohvat vozila s infinite-scrollom.
 * Prva stranica se (ponovno) dohvaća kad se promijeni pretraga; `loadMore` dodaje iduće stranice.
 */
export const usePaginatedVehicles = (search: string) => {
  const [vehicles, setVehicles] = useState<FleetVehicleRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Sprječava out-of-order rezultate kad se pretraga promijeni usred dohvata.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageNumber: number) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const res = await getFleetVehiclesAPI(
          // Sort "name" → backend default: marka → model → tip.
          { ...initialPagination, pageNumber, pageSize: PAGE_SIZE, sort: { ...initialPagination.sort, sortBy: "name" } },
          search
        );

        if (requestId !== requestIdRef.current) return;

        setVehicles((prev) => (pageNumber === 1 ? res.data : [...prev, ...res.data]));
        setTotalPages(
          res.pagination.totalPages ??
            Math.max(1, Math.ceil((res.pagination.totalCount ?? res.data.length) / PAGE_SIZE))
        );
        setPage(pageNumber);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [search]
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isLoading || page >= totalPages) return;
    fetchPage(page + 1);
  }, [isLoading, page, totalPages, fetchPage]);

  const refresh = useCallback(() => fetchPage(1), [fetchPage]);

  return { vehicles, isLoading, hasMore: page < totalPages, loadMore, refresh };
};

// Detalj vozila → red (samo polja koja dropdown opcija i tooltip koriste; `picture` iz `defaultImage`).
const toVehicleRow = (v: FleetVehicle): FleetVehicleRow =>
  ({ ...v, picture: v.defaultImage ?? null } as unknown as FleetVehicleRow);

/**
 * Dohvaća vozilo po id-u kad nije među već učitanim vozilima — da se preselektirana vrijednost
 * (ikona + tooltip) prikaže i kad je vozilo na nekoj kasnijoj stranici.
 */
export const usePreselectedVehicle = (
  value: string | null | undefined,
  loadedVehicles: FleetVehicleRow[]
): FleetVehicleRow | null => {
  const [vehicle, setVehicle] = useState<FleetVehicleRow | null>(null);
  const fetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!value) {
      setVehicle(null);
      fetchedIdRef.current = null;
      return;
    }
    if (fetchedIdRef.current === value) return;
    if (loadedVehicles.some((v) => v.id === value)) return;

    fetchedIdRef.current = value;
    let active = true;
    getFleetVehicleAPI(value)
      .then((v) => active && setVehicle(toVehicleRow(v)))
      .catch(() => active && setVehicle(null));

    return () => {
      active = false;
    };
    // loadedVehicles namjerno NIJE u deps — inače promjena stranice (novi dohvat) prekine ovaj dohvat prije nego stigne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return value ? vehicle : null;
};
